/**
 * WebSocketService - Centralized WebSocket management with best practices
 * 
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Message queuing during disconnection
 * - Event-based messaging system
 * - Connection state management
 * - Graceful degradation support
 */

import { getWebSocketUrl } from '../utils/api';
import { logger } from '../utils/logger';

export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting' | 'failed';

export interface WebSocketConfig {
  url: string;
  protocols?: string | string[];
  reconnect?: boolean;
  reconnectInterval?: number;
  maxReconnectInterval?: number;
  reconnectDecay?: number;
  maxReconnectAttempts?: number;
  binaryType?: BinaryType;
  enableQueuing?: boolean;
}

export interface WebSocketMessage {
  type: string;
  data?: any;
  timestamp?: number;
}

type MessageHandler = (message: WebSocketMessage) => void;
type StateChangeHandler = (state: ConnectionState) => void;
type ErrorHandler = (error: Event | Error) => void;

export class WebSocketService {
  private ws: WebSocket | null = null;
  private config: Required<WebSocketConfig>;
  private reconnectAttempts = 0;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private messageQueue: WebSocketMessage[] = [];
  private messageHandlers: Set<MessageHandler> = new Set();
  private stateChangeHandlers: Set<StateChangeHandler> = new Set();
  private errorHandlers: Set<ErrorHandler> = new Set();
  private _connectionState: ConnectionState = 'disconnected';
  private intentionallyClosed = false;
  private connectionPromise: Promise<void> | null = null;
  private connectionResolver: (() => void) | null = null;

  constructor(config: WebSocketConfig) {
    this.config = {
      url: config.url,
      reconnect: config.reconnect ?? true,
      reconnectInterval: config.reconnectInterval ?? 1000,
      maxReconnectInterval: config.maxReconnectInterval ?? 30000,
      reconnectDecay: config.reconnectDecay ?? 1.5,
      maxReconnectAttempts: config.maxReconnectAttempts ?? Infinity,
      binaryType: config.binaryType ?? 'blob',
      enableQueuing: config.enableQueuing ?? true,
      protocols: config.protocols ?? undefined
    } as Required<WebSocketConfig>;
  }

  get connectionState(): ConnectionState {
    return this._connectionState;
  }

  get isConnected(): boolean {
    return this._connectionState === 'connected' && this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Connect to WebSocket server
   * Returns a promise that resolves when connected or rejects on failure
   */
  async connect(): Promise<void> {
    if (this.isConnected) {
      return Promise.resolve();
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.intentionallyClosed = false;
    this.connectionPromise = new Promise((resolve, reject) => {
      this.connectionResolver = resolve;
      
      const timeout = setTimeout(() => {
        reject(new Error('WebSocket connection timeout'));
        this.connectionPromise = null;
        this.connectionResolver = null;
      }, 10000); // 10 second timeout

      this.createConnection();
      
      // Set up one-time handlers for this connection attempt
      const successHandler = () => {
        clearTimeout(timeout);
        resolve();
        this.connectionPromise = null;
        this.connectionResolver = null;
      };

      const failureHandler = () => {
        clearTimeout(timeout);
        reject(new Error('WebSocket connection failed'));
        this.connectionPromise = null;
        this.connectionResolver = null;
      };

      // Store handlers to remove them later
      const stateChangeHandler = (state: ConnectionState) => {
        if (state === 'connected') {
          this.offStateChange(stateChangeHandler);
          this.offError(errorHandler);
          successHandler();
        } else if (state === 'failed') {
          this.offStateChange(stateChangeHandler);
          this.offError(errorHandler);
          failureHandler();
        }
      };

      const errorHandler = () => {
        this.offStateChange(stateChangeHandler);
        this.offError(errorHandler);
        failureHandler();
      };

      this.onStateChange(stateChangeHandler);
      this.onError(errorHandler);
    });

    return this.connectionPromise;
  }

  /**
   * Connect without waiting - fire and forget
   */
  connectAsync(): void {
    this.connect().catch(err => {
      console.warn('WebSocket connection failed (non-blocking):', err);
    });
  }

  private createConnection(): void {
    if (this.ws?.readyState === WebSocket.CONNECTING || this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.updateState('connecting');

    try {
      if (this.config.protocols) {
        this.ws = new WebSocket(this.config.url, this.config.protocols);
      } else {
        this.ws = new WebSocket(this.config.url);
      }

      this.ws.binaryType = this.config.binaryType;

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      this.handleError(error as Event);
    }
  }

  private handleOpen(): void {
    logger.debug('WebSocket connected:', this.config.url);
    this.reconnectAttempts = 0;
    this.updateState('connected');

    // Send queued messages
    if (this.config.enableQueuing && this.messageQueue.length > 0) {
      logger.debug(`Sending ${this.messageQueue.length} queued messages`);
      const queue = [...this.messageQueue];
      this.messageQueue = [];
      queue.forEach(msg => this.send(msg));
    }

    if (this.connectionResolver) {
      this.connectionResolver();
    }
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message: WebSocketMessage = typeof event.data === 'string' 
        ? JSON.parse(event.data)
        : { type: 'binary', data: event.data };
      
      message.timestamp = Date.now();
      this.messageHandlers.forEach(handler => handler(message));
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error, event.data);
    }
  }

  private handleError(error: Event): void {
    console.error('WebSocket error:', error);
    this.errorHandlers.forEach(handler => handler(error));
  }

  private handleClose(event: CloseEvent): void {
    logger.debug('WebSocket closed:', event.code, event.reason);
    this.ws = null;

    if (this.intentionallyClosed) {
      this.updateState('disconnected');
    } else if (this.config.reconnect && this.reconnectAttempts < this.config.maxReconnectAttempts) {
      this.scheduleReconnect();
    } else {
      this.updateState('failed');
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    const interval = Math.min(
      this.config.reconnectInterval * Math.pow(this.config.reconnectDecay, this.reconnectAttempts),
      this.config.maxReconnectInterval
    );

    this.updateState('reconnecting');
    logger.debug(`Reconnecting in ${interval}ms (attempt ${this.reconnectAttempts + 1})`);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++;
      this.createConnection();
    }, interval);
  }

  private updateState(state: ConnectionState): void {
    if (this._connectionState !== state) {
      this._connectionState = state;
      this.stateChangeHandlers.forEach(handler => handler(state));
    }
  }

  /**
   * Send a message through the WebSocket
   * If not connected and queuing is enabled, the message will be queued
   */
  send(message: WebSocketMessage | string): boolean {
    const msg = typeof message === 'string' 
      ? { type: 'message', data: message }
      : message;

    if (this.isConnected && this.ws) {
      try {
        this.ws.send(JSON.stringify(msg));
        return true;
      } catch (error) {
        console.error('Failed to send WebSocket message:', error);
        if (this.config.enableQueuing) {
          this.messageQueue.push(msg);
        }
        return false;
      }
    } else if (this.config.enableQueuing) {
      logger.debug('WebSocket not connected, queuing message');
      this.messageQueue.push(msg);
      return false;
    }
    
    console.warn('WebSocket not connected and queuing disabled, message dropped');
    return false;
  }

  /**
   * Close the WebSocket connection
   */
  close(): void {
    this.intentionallyClosed = true;
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.messageQueue = [];
    this.updateState('disconnected');
  }

  /**
   * Event handlers
   */
  onMessage(handler: MessageHandler): void {
    this.messageHandlers.add(handler);
  }

  offMessage(handler: MessageHandler): void {
    this.messageHandlers.delete(handler);
  }

  onStateChange(handler: StateChangeHandler): void {
    this.stateChangeHandlers.add(handler);
  }

  offStateChange(handler: StateChangeHandler): void {
    this.stateChangeHandlers.delete(handler);
  }

  onError(handler: ErrorHandler): void {
    this.errorHandlers.add(handler);
  }

  offError(handler: ErrorHandler): void {
    this.errorHandlers.delete(handler);
  }

  /**
   * Cleanup all handlers
   */
  cleanup(): void {
    this.close();
    this.messageHandlers.clear();
    this.stateChangeHandlers.clear();
    this.errorHandlers.clear();
  }
}

/**
 * Factory function for creating WebSocket connections with standard config
 */
export function createWebSocketConnection(
  path: string, 
  options?: Partial<WebSocketConfig>
): WebSocketService {
  return new WebSocketService({
    url: getWebSocketUrl(path),
    ...options
  });
}